import { ZButton } from "@/shared/components/button";
import { ZInput } from "@/shared/components/input";
import { Modal, ModalContent } from "@nextui-org/react";
import { ArrowLeft, X } from "lucide-react";
import React, { useEffect, useState } from "react";
import { getControlSets, updateControlSet } from "../../requests";
import { addNotification } from "@/shared/states/notification";

interface SaveControlsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (name: string, overrideExisting?: boolean) => Promise<void>;
  onNavigateToOverwrite?: (name: string) => void;
  existingControlSets?: string[];
  controlsUrl?: string; // Add this prop to get the controls URL for override
  prePopulateName?: string; // Add prop for pre-populating the control set name
}

const SaveControlsModal: React.FC<SaveControlsModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onNavigateToOverwrite,
  existingControlSets = [],
  controlsUrl,
  prePopulateName
}) => {
  const [controlSetName, setControlSetName] = useState("");
  const [selectedExistingSet, setSelectedExistingSet] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  // For the demo, we'll start on step 2 by default
  const [showSecondStep, setShowSecondStep] = useState(false);

  // Reset modal state when it opens
  useEffect(() => {
    if (isOpen) {
      setControlSetName(prePopulateName || "");
      setSelectedExistingSet("");
      setIsSaving(false);
      setNameError("");
      setShowSecondStep(false);
    }
  }, [isOpen, prePopulateName]);

  const handleBack = () => {
    setShowSecondStep(false);
  };
  
  const handleEditName = () => {
    setShowSecondStep(false);
  };

  const handleSave = async () => {
    // Validate the control set name
    if (!controlSetName.trim()) {
      setNameError("Control set name is required");
      return;
    }

    // Check if name already exists
    if (existingControlSets.includes(controlSetName.trim())) {
      setShowSecondStep(true);
      return;
    }

    // Save the control set
    setIsSaving(true);
    setNameError("");

    try {
      await onSave(controlSetName.trim());
      onClose(); // Close modal on success
    } catch (error: any) {
      // More comprehensive error checking for HTTP errors
      const checkForAlreadyExists = (error: any): boolean => {
        // Check direct properties
        if (error?.message?.includes("already exists")) return true;
        if (error?.error_message?.includes("already exists")) return true;
        
        // Check nested properties
        if (error?.data?.error_message?.includes("already exists")) return true;
        if (error?.data?.message?.includes("already exists")) return true;
        
        // Check response data (for HTTP errors)
        if (error?.response?.data?.error_message?.includes("already exists")) return true;
        if (error?.response?.data?.message?.includes("already exists")) return true;
        
        // Check in stringified version
        const errorString = JSON.stringify(error);
        if (errorString.includes("already exists")) return true;
        
        return false;
      };
      
      if (checkForAlreadyExists(error)) {
        setShowSecondStep(true);
      } else {
        setNameError("Failed to save control set. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleOverrideExisting = async () => {

      setIsSaving(true);
      setNameError("");

      try {
        // Step 1: Get all control sets to find the matching one
        const controlSets = await getControlSets();
        const existingControlSet = controlSets.find(
          (set) => set.controls_set_name === controlSetName.trim()
        );

        if (!existingControlSet) {
          setNameError("Control set not found. Please try again.");
          setShowSecondStep(false);
          return;
        }

        // Step 2: Update the existing control set using the ID
        await updateControlSet(existingControlSet.id, {
          controls_url: controlsUrl as string,
          operation_type: "update_control_set_controls"
        });
        addNotification({
          message: `Control set '${controlSetName}' updated successfully!`,
          type: "success",
        });
        onClose(); // Close modal on success
      } catch (error) {

        setShowSecondStep(false); // Go back to first step on error
      } finally {
        setIsSaving(false);
      }

  };

  // Render the first step (naming step)
  const renderFirstStep = () => (
    <div className="p-6 flex flex-col gap-6">
      {/* Close button */}
      <button
        className="absolute right-3 top-3 text-[#71717A]"
        onClick={onClose}
      >
        <X size={16} />
      </button>
      
      {/* Header */}
      <div className="flex flex-col items-center">
        <h2 className="text-[18px] font-semibold leading-7 text-[#11181C]">
          Save Controls
        </h2>
      </div>
      
      {/* Form fields */}
      <div className="flex flex-col gap-4">
        {/* Create new control set name */}
        <div className="flex flex-col gap-1.5 w-[440px] h-[62px]">
          <label className="text-[14px] text-[#18181B] leading-5">
            Name of Control Set
          </label>
          <ZInput
            value={controlSetName}
            onChange={(e) => {
              setControlSetName(e.target.value);
              if (nameError) setNameError(""); // Clear error when user starts typing
            }}
            placeholder="Enter name of control set"
            variant="faded"
            className={`w-full ${nameError ? "border-[#F31260]" : ""}`}
            classNames={{
              inputWrapper: `w-full h-[36px] bg-[#FAFAFA] border border-[#E4E4E7] rounded-[8px] px-4 py-2 ${
                nameError ? "border-[#F31260]" : ""
              }`,
              input: "text-[14px] text-[#18181B] placeholder:text-[#A1A1AA]",
            }}
          />
          {nameError && (
            <p className="text-[14px] text-[#F31260] leading-5">
              {nameError}
            </p>
          )}
        </div>
      </div>
      
      {/* Footer with actions */}
      <div className="flex flex-col items-end gap-4">
        <div className="w-full border-t border-[#DCDCDC]"></div>
        <div className="flex justify-end gap-4">
          <ZButton
            variant="bordered"
            size="sm"
            className="border-1 text-[#006FEE]"
            onClick={onClose}
          >
            Cancel
          </ZButton>
          <ZButton
            color="primary"
            size="sm"
            onClick={handleSave}
            isLoading={isSaving}
            isDisabled={isSaving || !controlSetName.trim()} // Disable if saving or name is empty
          >
            {isSaving ? "Saving..." : "Save"}
          </ZButton>
        </div>
      </div>
    </div>
  );

  // Render the second step (name conflict step)
  const renderSecondStep = () => (
    <div className="py-6 px-6 flex flex-col items-center">
      <div className="w-[410px] flex flex-col gap-6">
        <div className="flex flex-col items-center gap-4">
          {/* Header with back button */}
          <div className="flex w-full items-center justify-center relative">
            <div className="absolute left-0">
              <ArrowLeft 
                size={24} 
                className="cursor-pointer" 
                onClick={handleBack}
              />
            </div>
            <h2 className="text-[18px] font-semibold leading-7 text-center text-[#11181C]">
              Name already in use
            </h2>
          </div>
          
          {/* Description */}
          <p className="text-[14px] text-[#777780] text-center">
            The name "{controlSetName.trim()}" is already being used in your organization. You can either save with a new name or override the existing one.
          </p>
        </div>
        
        {/* Action buttons */}
        <div className="flex flex-row w-full gap-4 mt-6">
          <ZButton
            variant="bordered"
            size="md"
            className="flex-1 h-10 text-[16px] border-1 border-[#006FEE] text-[#006FEE]"
            onClick={handleEditName}
          >
            Edit Name
          </ZButton>
          <ZButton
            color="primary"
            size="md"
            className="flex-1 h-10 text-[16px]"
            onClick={handleOverrideExisting}
            isLoading={isSaving}
            isDisabled={isSaving}
          >
            {isSaving ? "Saving..." : "Override Existing"}
          </ZButton>
        </div>
      </div>
    </div>
  );

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      hideCloseButton
      className={showSecondStep ? "max-w-[458px]" : "max-w-[488px]"}
    >
      <ModalContent className="p-0">
        {() => (showSecondStep ? renderSecondStep() : renderFirstStep())}
      </ModalContent>
    </Modal>
  );
};

export default SaveControlsModal;